import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils.ts";
import { sessionAtom } from "@/state/auth.state.ts";
import type { OAuthMessage } from "@/types/oauth.types.ts";
import type { StravaScope } from "@/types/strava.types.ts";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useAtomValue } from "jotai";
import posthog from "posthog-js";
import { useMemo, useState } from "react";
import { supabase } from "../../lib/supabase.ts";

const clientId = import.meta.env.VITE_STRAVA_CLIENT_ID;
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

type ConnectStravaButtonProps = {
  scope?: StravaScope[];
  force?: boolean;
  onSuccess?: () => void;
};
export function ConnectStravaButton({
  scope = ["read", "activity:read", "activity:read_all"],
  force = false,
  onSuccess,
}: ConnectStravaButtonProps) {
  const session = useAtomValue(sessionAtom);

  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeAthleteId, setMergeAthleteId] = useState<number | null>(null);

  const canConnect = useMemo(() => !loading && !merging, [loading, merging]);

  async function connect() {
    if (!canConnect) return;

    posthog.capture("connect_strava_started");

    setLoading(true);
    setError(null);

    let popup: Window | null = null;
    let checkPopupClosed: number | undefined;

    const cleanup = (handleMessage: (e: MessageEvent) => void) => {
      window.removeEventListener("message", handleMessage);
      if (checkPopupClosed) window.clearInterval(checkPopupClosed);
      checkPopupClosed = undefined;
      popup?.close();
    };

    try {
      const { data } = await supabase.auth.getUser();
      const user = data.user;
      if (!user) {
        setError(t`Please sign in first.`);
        setLoading(false);
        return;
      }

      const state = crypto.randomUUID();

      const { error: insertError } = await supabase
        .from("oauth_states")
        .insert({ state, user_id: user.id, origin: window.location.origin });

      if (insertError) throw insertError;

      const redirectUri = `${supabaseUrl}/functions/v1/strava-oauth-callback`;

      const params = new URLSearchParams({
        client_id: clientId,
        response_type: "code",
        redirect_uri: redirectUri,
        approval_prompt: force ? "force" : "auto",
        scope: scope.join(","),
        state,
      });

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      popup = window.open(
        `https://www.strava.com/oauth/authorize?${params.toString()}`,
        "strava-oauth",
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      if (!popup) {
        throw new Error(t`Popup blocked. Please allow popups for this site.`);
      }

      const handleMessage = (event: MessageEvent) => {
        if (event.source !== popup) return;
        if (event.origin !== window.location.origin) return;

        const msg = event.data as OAuthMessage;
        if (!msg?.type) return;

        if (msg.type === "oauth-success") {
          posthog.capture("connect_strava_completed");

          setLoading(false);
          cleanup(handleMessage);
          void supabase.auth.refreshSession();
          onSuccess?.();
          return;
        }

        if (msg.type === "oauth-error") {
          // Special case: Strava athlete already linked to another user
          if (msg.reason === "already_linked") {
            const idNum = Number(msg.athleteId);
            if (Number.isFinite(idNum) && idNum > 0) {
              setMergeAthleteId(idNum);
              setMergeDialogOpen(true);
              setError(null);
            } else {
              setError(
                t`This Strava account is already linked to another account.`,
              );
            }
            setLoading(false);
            cleanup(handleMessage);
            return;
          }

          setError(msg.error || t`Connection failed`);
          setLoading(false);
          cleanup(handleMessage);
          return;
        }
      };

      window.addEventListener("message", handleMessage);

      checkPopupClosed = window.setInterval(() => {
        if (!popup || popup.closed) {
          cleanup(handleMessage);
          setLoading(false);
          setError(t`Connection cancelled`);
        }
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : t`An error occurred`);
      setLoading(false);
      popup?.close();
      if (checkPopupClosed) window.clearInterval(checkPopupClosed);
    }
  }

  async function confirmMerge() {
    if (!mergeAthleteId || !session) return;

    setMerging(true);
    setError(null);

    try {
      await fetch(`${supabaseUrl}/functions/v1/merge-strava-accounts`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ athleteId: mergeAthleteId }),
      });

      // After merge, the current user may be deleted → session is invalid.
      await supabase.auth.signOut();
      setMergeDialogOpen(false);
      setMergeAthleteId(null);

      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t`Merge failed`);
    } finally {
      setMerging(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="relative bg-[#FC5200]" onClick={connect}>
        <img
          src="/btn_strava_connect_with_orange.png"
          alt="Sign in with Strava"
          className={cn(
            "max-h-[48px] cursor-pointer",
            !canConnect && "opacity-0",
          )}
        />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-white opacity-70">
            <Trans>Connecting...</Trans>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <AlertDialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Strava account already linked</Trans>
            </AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>
                This Strava account is already connected to another Stratistics
                account. You can merge the accounts by moving your email login
                onto that account. After merging, you'll be signed out and asked
                to log in again. No activities or settings will be lost.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={merging}>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button onClick={confirmMerge} disabled={merging}>
                {merging ? (
                  <Trans>Merging...</Trans>
                ) : (
                  <Trans>Merge accounts</Trans>
                )}
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
